import { useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { DashboardApi } from '../services/api';
import { notifyInfo } from '../utils/statusShortcuts';

const MAX_NOTIFICATIONS_PER_LOAD = 3;

/**
 * Shows a one-time toast for changelog entries marked as announceable by the API
 * (`announce`: notify enabled, not expired, and among the 3 most recent
 * announcements), and marks them as seen per account (server-side, cross-device).
 */
export default function useChangelogNotifications() {
  const { isAuthenticated, isImpersonating, activeUserId } = useAuth();
  const hasRunRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || isImpersonating) return undefined;
    if (hasRunRef.current) return undefined;
    hasRunRef.current = true;

    (async () => {
      try {
        const data = await DashboardApi.getChangelog();
        const entries = Array.isArray(data?.entries) ? data.entries : [];
        const unseen = entries.filter((entry) => entry.announce && !entry.seen);

        unseen.slice(0, MAX_NOTIFICATIONS_PER_LOAD).forEach((entry) => {
          notifyInfo(entry.summary || entry.title, {
            title: entry.version ? `Endringslogg ${entry.version}` : 'Endringslogg',
            source: 'Endringslogg',
            actionLabel: 'Les mer',
            actionHref: '/endringslogg',
            dedupeKey: `changelog:${entry.id}`,
            durationMs: 8000,
          });
          DashboardApi.markChangelogSeen(entry.id).catch(() => {});
        });
      } catch (_error) {
        // Changelog notifications are best-effort; ignore failures.
      }
    })();

    return undefined;
  }, [isAuthenticated, isImpersonating, activeUserId]);
}
